import React, { useMemo, useState } from "react";
import { CONVERTER_GROUPS, convert, formatNumber } from "@/lib/converters";
import { SafetyBanner } from "@/components/SafetyBanner";
import { ArrowDownUp } from "lucide-react";

const GROUP_LABELS = {
  length: "Length",
  pressure: "Pressure",
  volume: "Volume",
  density: "Density",
  flow: "Flow Rate",
  temp: "Temperature",
};

const DEFAULTS = {
  length: { from: "ft", to: "m" },
  pressure: { from: "psi", to: "bar" },
  volume: { from: "bbl", to: "m3" },
  density: { from: "ppg", to: "sg" },
  flow: { from: "gpm", to: "lpm" },
  temp: { from: "F", to: "C" },
};

export default function Converters() {
  const [group, setGroup] = useState("length");
  const [from, setFrom] = useState(DEFAULTS.length.from);
  const [to, setTo] = useState(DEFAULTS.length.to);
  const [value, setValue] = useState("");

  const onGroup = (g) => {
    setGroup(g);
    setFrom(DEFAULTS[g].from);
    setTo(DEFAULTS[g].to);
  };

  const result = useMemo(() => {
    if (value === "" || value === null) return "";
    try {
      const r = convert(group, value, from, to);
      return formatNumber(r, 6);
    } catch {
      return "";
    }
  }, [value, group, from, to]);

  const swap = () => {
    setFrom(to); setTo(from);
  };

  const units = Object.entries(CONVERTER_GROUPS[group].units);

  return (
    <div className="flex flex-col gap-5" data-testid="converters-page">
      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">Unit Converters</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mt-1">Convert</h2>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5">Length, pressure, volume, density, flow rate, and temperature.</p>
      </section>

      <div className="flex gap-2 overflow-x-auto hide-sb -mx-1 px-1">
        {Object.keys(CONVERTER_GROUPS).map((g) => (
          <button
            key={g}
            data-testid={`group-${g}`}
            onClick={() => onGroup(g)}
            className={`unit-pill whitespace-nowrap`}
            data-active={group === g}
          >
            {GROUP_LABELS[g]}
          </button>
        ))}
      </div>

      <section className="card-rs p-4">
        <div className="label-up text-[var(--rs-text-3)] mb-3">From</div>
        <input
          data-testid="conv-input"
          type="number"
          inputMode="decimal"
          className="input-rs text-2xl font-mono-rs"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="0"
        />
        <div className="flex flex-wrap gap-2 mt-3">
          {units.map(([k, u]) => (
            <button
              key={k}
              data-testid={`from-${k}`}
              data-active={from === k}
              onClick={() => setFrom(k)}
              className="unit-pill"
            >
              {u.name}
            </button>
          ))}
        </div>

        <button data-testid="swap-btn" onClick={swap} className="btn-ghost h-10 px-3 mt-4 rounded-md inline-flex items-center gap-2 text-xs label-up">
          <ArrowDownUp className="w-4 h-4" /> Swap
        </button>

        <div className="label-up text-[var(--rs-text-3)] mt-5 mb-3">To</div>
        <div
          data-testid="conv-result"
          className="w-full font-mono-rs text-3xl font-bold tracking-tighter text-[var(--rs-amber)] py-3 px-3 rounded-md bg-[var(--rs-bg)] border border-[var(--rs-border)] min-h-[60px] flex items-center"
        >
          {result || "—"}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {units.map(([k, u]) => (
            <button
              key={k}
              data-testid={`to-${k}`}
              data-active={to === k}
              onClick={() => setTo(k)}
              className="unit-pill"
            >
              {u.name}
            </button>
          ))}
        </div>
      </section>

      <SafetyBanner compact />
    </div>
  );
}
